/**
 * New Backtest Route
 * Configure and run a new backtest
 */

import { createFileRoute, useNavigate, Link, useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import { useAppStore } from '@/lib/store'
import { BacktestForm } from '@/components/backtesting/backtest-form'
import { BacktestResults } from '@/components/backtesting/backtest-results'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/trading/card'
import { RotateCcw } from 'lucide-react'

export const Route = createFileRoute('/dashboard/backtests/new')({
  component: NewBacktest,
})

function NewBacktest() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/dashboard/backtests/new' })
  const { state, actions } = useAppStore()
  const [isRunning, setIsRunning] = useState(false)
  const [backtestResults, setBacktestResults] = useState<any>(null)

  // Get algorithm from query params
  const algorithmId = search.algorithmId as string | undefined
  const algorithm = state.algorithms.find((a) => a.id === algorithmId)

  const handleStart = (config: any) => {
    setIsRunning(true)

    // Create new backtest
    const newBacktest = {
      id: Date.now().toString(),
      algorithmId: config.algorithmId,
      name: config.name,
      date: new Date().toISOString(),
      status: 'running',
      configuration: config,
    }

    actions.startBacktest(newBacktest)

    // Simulate backtest completion (in production, this would be a real API call)
    setTimeout(() => {
      const mockResults = {
        finalCapital: config.initialCapital * (1 + (Math.random() - 0.4) * 0.3),
        totalReturn: config.initialCapital * (Math.random() - 0.4) * 0.3,
        totalReturnPercent: (Math.random() - 0.4) * 30,
        winRate: 45 + Math.random() * 25,
        profitFactor: 1 + Math.random() * 2,
        maxDrawdown: 5 + Math.random() * 20,
        sharpeRatio: -0.5 + Math.random() * 3,
        totalTrades: Math.floor(20 + Math.random() * 80),
        equityCurve: [],
        trades: [],
      }

      // Generate equity curve
      let value = config.initialCapital
      for (let i = 0; i < 100; i++) {
        value = value + (Math.random() - 0.45) * config.initialCapital * 0.01
        mockResults.equityCurve.push({
          date: new Date(Date.now() - (100 - i) * 24 * 60 * 60 * 1000).toISOString(),
          value,
        })
      }

      // Generate trades
      for (let i = 0; i < mockResults.totalTrades; i++) {
        mockResults.trades.push({
          id: `${i}`,
          symbol: config.symbol,
          type: Math.random() > 0.5 ? 'long' : 'short',
          entryPrice: 1 + Math.random() * 0.1,
          exitPrice: 1 + Math.random() * 0.1,
          quantity: 1,
          entryDate: new Date(Date.now() - mockResults.totalTrades * 24 * 60 * 60 * 1000 + i * 24 * 60 * 60 * 1000).toISOString(),
          exitDate: new Date(Date.now() - mockResults.totalTrades * 24 * 60 * 60 * 1000 + (i + 1) * 24 * 60 * 60 * 1000).toISOString(),
          pnl: (Math.random() - 0.4) * 200,
          status: 'closed',
        })
      }

      actions.updateBacktest(newBacktest.id, {
        status: 'completed',
        results: mockResults,
      })

      setBacktestResults(mockResults)
      setIsRunning(false)
    }, 3000)
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/dashboard/backtests">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Backtests
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold">Run Backtest</h1>
          <p className="text-muted-foreground">Configure and execute a backtest</p>
        </div>
      </div>

      {!isRunning && !backtestResults && (
        <BacktestForm algorithm={algorithm} onStart={handleStart} />
      )}

      {isRunning && (
        <Card>
          <CardContent className="py-12 text-center">
            <RotateCcw className="h-16 w-16 mx-auto mb-6 text-primary animate-spin" />
            <h2 className="text-xl font-semibold mb-2">Running Backtest</h2>
            <p className="text-muted-foreground mb-4">Simulating trades with historical data...</p>
            <div className="flex items-center justify-center gap-2">
              <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
              <div className="h-2 w-2 rounded-full bg-primary animate-pulse delay-100" />
              <div className="h-2 w-2 rounded-full bg-primary animate-pulse delay-200" />
            </div>
          </CardContent>
        </Card>
      )}

      {backtestResults && !isRunning && (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Backtest Results</h2>
            <Button onClick={() => {
              setBacktestResults(null)
            }}>
              Run Another
            </Button>
          </div>
          <BacktestResults results={backtestResults} configuration={{ initialCapital: 10000 }} />
        </>
      )}
    </div>
  )
}
